// continuous feedback is split into 4 subfeatures, so we need
// to ensure that at least one of those subfeatures is enabled
// for the high level feature to be enabled at all
// NOTE: some clients may be define "{"continuous_feedback": {"enabled": true}}"
// explicitly without providing the below sub-items, so we default them all to enabled

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getCampaignHasFeatureEnabled,
  PERFORMANCE_FEATURE_HIDE_RATING_FROM_DIRECT_REPORT,
  PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER,
  PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_ALLOW_CALIBRATION,
  PERFORMANCE_FEATURE_SHOW_RATING_COMMENTS_TO_DIRECT_REPORT,
} from 'utils/models/PerformanceUtils';
import { Campaign, Features, ReduxState } from '../../types';

export const engagementIsEnabled = (features: Features): boolean => {
  return features?.engagement?.enabled;
};

export const performanceFlowForEndUsersIsEnabled = (
  features: Features
): boolean => {
  return (
    features?.performance?.enabled &&
    (features?.performance?.complete_survey ?? true)
  );
};

export const surveysAndPerformanceNavIsEnabled = (
  features: Features
): boolean => {
  return (
    features?.performance?.enabled &&
    features?.performance?.surveys_and_performance
  );
};

export const ratingDashboardIsEnabled = (features: Features): boolean => {
  return (
    features?.performance?.enabled &&
    (features?.performance?.rating_dashboard ?? true)
  );
};

export const performanceProfilesAreEnabled = (features: Features): boolean => {
  return (
    features?.performance?.enabled &&
    features?.performance?.performance_profiles
  );
};

export const teamDashboardIsEnabled = (features: Features): boolean => {
  return (
    features?.performance?.enabled && features?.performance?.team_dashboard
  );
};

export const participationDashboardIsEnabled = (
  features: Features
): boolean => {
  return (
    features?.performance?.enabled &&
    features?.performance?.participation_dashboard
  );
};

export const calibrationDashboardIsEnabled = (features: Features): boolean => {
  return (
    features?.performance?.enabled &&
    features?.performance?.calibration_dashboard
  );
};

export const keyTakeawaysIsEnabled = (features: Features): boolean => {
  return features?.performance?.enabled && features?.performance?.key_takeaways;
};

export const isInfluenceReportingEnabled = (features: Features): boolean => {
  return (
    features?.ona_reporting?.enabled !== false &&
    features?.ona_reporting?.show_influence !== false
  );
};

export const isGoldStarsReportingEnabled = (features: Features): boolean => {
  return (
    features?.ona_reporting?.enabled !== false &&
    features?.ona_reporting?.show_gold_stars !== false
  );
};

export const isHeadsUpsReportingEnabled = (features: Features): boolean => {
  return (
    features?.ona_reporting?.enabled !== false &&
    features?.ona_reporting?.show_heads_ups !== false
  );
};

export const unifiedIntercomIsEnabled = (features: Features): boolean => {
  return !!features?.unified_intercom?.enabled;
};

export const resumesAreEnabled = (features: Features): boolean => {
  return !!features?.resumes?.enabled;
};

export const oneOnOnesAreEnabled = (features: Features): boolean => {
  return !!features?.one_on_ones?.enabled;
};

export const objectivesAreEnabled = (features: Features): boolean => {
  return !!features?.objectives?.enabled;
};

export const objectivesInDashboardAreEnabled = (
  features: Features
): boolean => {
  return !!(
    features?.objectives?.enabled && features?.objectives?.show_in_dashboard
  );
};

export const goalsAreEnabled = (features: Features): boolean => {
  return !!features?.goals?.enabled;
};

export const operatingManualsAreEnabled = (features: Features): boolean => {
  return !!features?.operating_manual?.enabled;
};

export const profileSkillCloudsAreEnabled = (features: Features): boolean => {
  return (
    features.skills_and_behaviors?.enabled &&
    features.skills_and_behaviors?.profile_word_clouds
  );
};

export const profileIntroIsEnabled = (features: Features): boolean => {
  return features?.profile_intro?.enabled ?? true;
};

// unless they are explicitly set to false
export const atLeastOneContinuousFeedbackFeatureIsEnabled = (
  features?: Features
) => {
  return (
    features?.continuous_feedback?.enabled &&
    (features?.continuous_feedback?.recognition_enabled !== false ||
      features?.continuous_feedback?.private_notes_enabled !== false ||
      // note: we don't check requesting because if that one is true but
      // giving is false, there's no way to respond to requests, so we
      // don't want to enable the feature in that case
      features?.continuous_feedback?.giving_feedback_enabled !== false)
  );
};

export const activitiesAreEnabled = (features: Features) => {
  return features?.activities?.enabled !== false;
};

export const activitiesInHomepageFeedIsEnabled = (features: Features) => {
  return (
    features?.activities?.enabled !== false &&
    features?.activities?.show_in_dashboard !== false
  );
};

export const continuousFeedbackInHomepageFeedIsEnabled = (
  features: Features
) => {
  return (
    atLeastOneContinuousFeedbackFeatureIsEnabled(features) &&
    features?.continuous_feedback?.show_in_dashboard !== false
  );
};

export const pulseChecksAreEnabled = (features: Features): boolean => {
  // note: "show_in_dashboard" for pulse checks refers to the feed, while
  // this refers to the feature existing at all (i.e. in the right sidebar
  // of the homepage where a person fills out the pulse check)
  return !!features?.pulse_checks?.enabled;
};

export const pulseChecksInHomepageFeedIsEnabled = (features: Features) => {
  return (
    features?.pulse_checks?.enabled !== false &&
    features?.pulse_checks?.show_in_dashboard !== false
  );
};

export const anyHomepageFeedItemsAreEnabled = (features: Features) => {
  return (
    activitiesInHomepageFeedIsEnabled(features) ||
    continuousFeedbackInHomepageFeedIsEnabled(features) ||
    pulseChecksInHomepageFeedIsEnabled(features)
  );
};

export const continuousFeedbackRecognitionIsEnabled = (
  features: Features
): boolean => {
  return !!(
    features?.continuous_feedback?.enabled &&
    features?.continuous_feedback?.recognition_enabled !== false
  );
};

export const continuousFeedbackPrivateNotesAreEnabled = (
  features: Features
): boolean => {
  return !!(
    features?.continuous_feedback?.enabled &&
    features?.continuous_feedback?.private_notes_enabled !== false
  );
};

export const continuousFeedbackGivingFeedbackIsEnabled = (
  features: Features
): boolean => {
  return !!(
    features?.continuous_feedback?.enabled &&
    features?.continuous_feedback?.giving_feedback_enabled !== false
  );
};

export const continuousFeedbackRequestsAreEnabled = (
  features: Features
): boolean => {
  // BOTH giving AND requesting feedback need to be enabled, because if
  // giving is disabled, there's no point in requesting as the request
  // cannot be fulfilled by the person receiving it
  return !!(
    features?.continuous_feedback?.enabled &&
    features?.continuous_feedback?.giving_feedback_enabled !== false &&
    features?.continuous_feedback?.feedback_requests_enabled !== false
  );
};

export const promotionPacketsAreEnabled = (features: Features): boolean => {
  // must be explicitly enabled
  return !!features?.promotion_packets?.enabled;
};

export const promotionPacketFeedbackRequestsAreEnabled = (
  features: Features
): boolean => {
  return (
    continuousFeedbackRequestsAreEnabled(features) &&
    promotionPacketsAreEnabled(features) &&
    // we default this to true; must be explicitly disabled
    features?.promotion_packets?.feedback_requests_enabled !== false
  );
};

export const continuousFeedbackRequestCustomQuestionsAreEnabled = (
  features: Features
): boolean => {
  return !!(
    continuousFeedbackRequestsAreEnabled(features) &&
    features?.continuous_feedback?.feedback_request_custom_questions_enabled !==
      false
  );
};

export type FeatureContext = {
  features: Features;
  ifEnabled: <T>(
    featureName: keyof Features,
    object: T,
    valueIfDisabled: T
  ) => T;
  ifOptionEnabled: <T, N extends keyof Features, O extends keyof Features[N]>(
    featureName: N,
    featureOption: O,
    object: T,
    valueIfDisabled: T
  ) => T;
  isEnabled: (featureName: keyof Features) => boolean;
  isOptionEnabled: <N extends keyof Features, O extends keyof Features[N]>(
    featureName: N,
    featureOption: O
  ) => boolean;
  isGroupAndOptionEnabled: <
    N extends keyof Features,
    O extends keyof Features[N],
  >(
    featureName: N,
    featureOption: O
  ) => boolean;
};

export const useFeatures = (): FeatureContext => {
  const features = useSelector<ReduxState, Features>((state) => state.features);

  const ifEnabled = useCallback(
    (featureName, object, valueIfDisabled) =>
      (features ?? {})[featureName]?.enabled ? object : valueIfDisabled,
    [features]
  );

  const ifOptionEnabled = useCallback(
    (featureName, featureOption, object, valueIfDisabled) =>
      (features ?? {})[featureName]?.[featureOption] ? object : valueIfDisabled,
    [features]
  );

  const isEnabled = useCallback(
    (featureName) => (features ?? {})[featureName]?.enabled ?? false,
    [features]
  );

  const isOptionEnabled = useCallback(
    (featureName, featureOption) =>
      (features ?? {})[featureName]?.[featureOption] ?? false,
    [features]
  );

  const isGroupAndOptionEnabled = useCallback(
    (featureName, featureOption) =>
      (isEnabled(featureName) &&
        (features ?? {})[featureName]?.[featureOption]) ??
      false,
    [features, isEnabled]
  );

  const featuresContext = useMemo(
    () => ({
      features,
      ifEnabled,
      isEnabled,
      isOptionEnabled,
      isGroupAndOptionEnabled,
      ifOptionEnabled,
    }),
    [
      features,
      ifEnabled,
      isEnabled,
      isOptionEnabled,
      ifOptionEnabled,
      isGroupAndOptionEnabled,
    ]
  );

  return featuresContext;
};

export const isSkillsAndBehaviorsDirectoryEnabled = (
  features: Features
): boolean => {
  return (
    (features?.skills_and_behaviors?.enabled &&
      features?.skills_and_behaviors?.list_view) ??
    true
  );
};

export const areSkillsAndBehaviorsPagesEnabled = (
  features: Features
): boolean => {
  return features?.skills_and_behaviors?.enabled ?? true;
};

export const areListingPeopleInSkillsAndBehaviorsDirectoryEnabled = (
  features: Features
): boolean => {
  return (
    (areSkillsAndBehaviorsPagesEnabled(features) &&
      features?.skills_and_behaviors?.people_list) ??
    true
  );
};

export const areListingActivitiesInSkillsAndBehaviorsDirectoryEnabled = (
  features: Features
): boolean => {
  return (
    (areSkillsAndBehaviorsPagesEnabled(features) &&
      features?.activities?.enabled &&
      features?.skills_and_behaviors?.activity_list) ??
    true
  );
};

export const isSkillsTalentInventoryEnabled = (features: Features): boolean => {
  return features?.skills_talent_inventory?.enabled ?? true;
};

export const isTalentMatricesEnabled = (features: Features): boolean => {
  return features?.talent_matrices?.enabled ?? true;
};

export const isAdditionalManagersEnabled = (features: Features): boolean => {
  return features.additional_managers.enabled;
};

export const isExternalManagerRatingsEnabled = (
  features: Features
): boolean => {
  return features?.external_manager_ratings?.enabled ?? false;
};

export const isAIAgentsEnabled = (features: Features): boolean => {
  return features?.ai_assistant?.ai_agents ?? false;
};

export const isExtendedCalibrationEnabled = (features: Features): boolean =>
  features?.extended_calibration?.enabled ?? false;

export const isCalibratePromotionNominations = (campaign: Campaign): boolean =>
  !!campaign &&
  getCampaignHasFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER
  ) &&
  getCampaignHasFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_PROMOTION_NOMINATION_BY_MANAGER_ALLOW_CALIBRATION
  );

export const isHideRatingCommentsToDirectReport = (
  campaign: Campaign
): boolean =>
  !campaign ||
  getCampaignHasFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_HIDE_RATING_FROM_DIRECT_REPORT
  ) ||
  !getCampaignHasFeatureEnabled(
    campaign,
    PERFORMANCE_FEATURE_SHOW_RATING_COMMENTS_TO_DIRECT_REPORT
  );
